import type { PathsWithMethod } from "openapi-typescript-helpers"
import { API_BASE_URL, apiFetch } from "@/api/client"
import type { paths } from "@/api/schema"

export type ExportPath = Extract<
	PathsWithMethod<paths, "get">,
	`${string}/export`
>

export type ExportParams<P extends ExportPath> = paths[P]["get"] extends {
	parameters: { query?: infer Q }
}
	? NonNullable<Q>
	: never

export function downloadBlob(blob: Blob, filename: string): void {
	const url = URL.createObjectURL(blob)
	const a = document.createElement("a")
	a.href = url
	a.download = filename
	document.body.appendChild(a)
	a.click()
	document.body.removeChild(a)
	URL.revokeObjectURL(url)
}

export function parseContentDispositionFilename(
	disposition: string,
): string | null {
	if (!disposition) return null

	const filename = disposition.match(/filename\s*=\s*("[^"]+"|[^;]+)/i)?.[1]
	if (!filename) return null

	const unquoted = filename.trim().replace(/^"(.*)"$/, "$1")
	return unquoted.split(/[\\/]/).pop() ?? unquoted
}

export async function downloadResponse(
	response: Response,
	fallbackFilename = "download",
): Promise<void> {
	const disposition = response.headers.get("Content-Disposition") ?? ""
	const filename =
		parseContentDispositionFilename(disposition) ?? fallbackFilename
	downloadBlob(await response.blob(), filename)
}

export async function exportXlsx<P extends ExportPath>(
	path: P,
	params: ExportParams<P>,
): Promise<void> {
	const search = new URLSearchParams()
	for (const [key, value] of Object.entries(params)) {
		if (value === undefined || value === null || value === "") continue
		if (Array.isArray(value)) {
			for (const item of value) {
				if (item !== undefined && item !== null && item !== "") {
					search.append(key, String(item))
				}
			}
		} else {
			search.set(key, String(value))
		}
	}
	const qs = search.toString()
	const response = await apiFetch(`${API_BASE_URL}${path}${qs ? `?${qs}` : ""}`)
	await downloadResponse(response, "export.xlsx")
}
